// services/eventService.js - Web version
import supabase from './supabase';

// Constants
const APP_SOURCE = 'invits';
const SOURCE_PLATFORM = 'web';

/**
 * Generate unique slug for event
 */
export const generateUniqueSlug = async (title) => {
  const baseSlug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50);

  let slug = baseSlug;
  let counter = 1;

  // Check if slug exists
  while (true) {
    const { data, error } = await supabase
      .from('events')
      .select('id')
      .eq('shareable_slug', slug)
      .single();

    if (error && error.code === 'PGRST116') {
      // Slug doesn't exist, we can use it
      break;
    }

    if (data) {
      // Slug exists, try with counter
      slug = `${baseSlug}-${counter}`;
      counter++;
    }
  }

  return slug;
};

/**
 * Get current financial year
 */
export const getCurrentFinancialYear = async () => {
  try {
    const { data, error } = await supabase.rpc('get_current_financial_year');
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting financial year:', error);
    // Fallback to calculating it manually
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // JavaScript months are 0-indexed
    
    // Financial year starts in April (month 4)
    if (month >= 4) {
      return `FY${year}-${year + 1}`;
    } else {
      return `FY${year - 1}-${year}`;
    }
  }
};

/**
 * Compress image for web
 * Uses browser's canvas API for compression
 */
const compressImage = async (file, maxWidth = 1920) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/jpeg',
        0.85 // Quality
      );
    };

    img.onerror = reject;
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Upload event cover image to Supabase Storage
 * Web version - uses File API instead of expo-file-system
 */
export const uploadEventCoverImage = async (imageData, eventId) => {
  try {
    console.log('📸 Processing cover image for upload...');

    if (!imageData || !imageData.uri) {
      throw new Error('Invalid image data: missing URI');
    }

    // Handle different image types
    if (imageData.isAnimated) {
      // === ANIMATED GIF FLOW ===
      console.log('🎬 Processing animated GIF...');
      
      // For web, if it's already a File object, use it directly
      // If it's a data URL or blob URL, fetch it
      let gifBlob;
      
      if (imageData.file) {
        gifBlob = imageData.file;
      } else if (imageData.uri.startsWith('blob:') || imageData.uri.startsWith('http')) {
        const response = await fetch(imageData.uri);
        gifBlob = await response.blob();
      } else if (imageData.uri.startsWith('data:')) {
        const response = await fetch(imageData.uri);
        gifBlob = await response.blob();
      } else {
        throw new Error('Invalid GIF URI format');
      }

      // Upload GIF
      const gifFilename = `${eventId}/cover.gif`;
      const { data: gifData, error: gifError } = await supabase.storage
        .from('event-media')
        .upload(gifFilename, gifBlob, {
          contentType: 'image/gif',
          upsert: true,
        });

      if (gifError) throw gifError;

      const { data: { publicUrl: gifPublicUrl } } = supabase.storage
        .from('event-media')
        .getPublicUrl(gifFilename);

      // Create static frame for fallback (optional - you can enhance this)
      // For now, we'll just use the GIF URL for both
      
      return {
        type: 'gif',
        gif: {
          b2Url: gifPublicUrl,
          cdnUrl: gifPublicUrl,
          path: gifFilename,
        },
        staticGif: {
          b2Url: gifPublicUrl,
          cdnUrl: gifPublicUrl,
          path: gifFilename,
        },
      };

    } else {
      // === STATIC IMAGE FLOW ===
      console.log('🖼️ Processing static image...');

      let imageBlob;

      // Handle File object or data URL
      if (imageData.file) {
        // Compress the file
        imageBlob = await compressImage(imageData.file, 1920);
      } else if (imageData.uri.startsWith('blob:') || imageData.uri.startsWith('http')) {
        const response = await fetch(imageData.uri);
        const blob = await response.blob();
        imageBlob = await compressImage(blob, 1920);
      } else if (imageData.uri.startsWith('data:')) {
        const response = await fetch(imageData.uri);
        const blob = await response.blob();
        imageBlob = await compressImage(blob, 1920);
      } else if (imageData.uri.startsWith('/')) {
        // It's a local path (default background)
        const response = await fetch(imageData.uri);
        const blob = await response.blob();
        imageBlob = await compressImage(blob, 1920);
      } else {
        throw new Error('Invalid image URI format');
      }

      // Upload to Supabase Storage
      const filename = `${eventId}/cover.jpg`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('event-media')
        .upload(filename, imageBlob, {
          contentType: 'image/jpeg',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('event-media')
        .getPublicUrl(filename);

      console.log('✅ Image uploaded successfully');

      return {
        type: 'image',
        cover: {
          b2Url: publicUrl,
          cdnUrl: publicUrl,
          path: filename,
        },
      };
    }

  } catch (error) {
    console.error('❌ Error uploading cover image:', error);
    throw error;
  }
};

/**
 * Update event with cover image URLs
 */
export const updateEventCoverImage = async (eventId, financialYear, uploadResult) => {
  try {
    console.log('🔄 Updating event with cover image...');

    let updateData = {
      cover_image_type: uploadResult.type,
      updated_at: new Date().toISOString(),
    };

    if (uploadResult.type === 'gif') {
      updateData = {
        ...updateData,
        gif_cover_cdn_url: uploadResult.gif.cdnUrl,
        gif_cover_direct_url: uploadResult.gif.b2Url,
        gif_cover_path: uploadResult.gif.path,
        static_gif_cdn_url: uploadResult.staticGif.cdnUrl,
        static_gif_direct_url: uploadResult.staticGif.b2Url,
        static_gif_path: uploadResult.staticGif.path,
        cover_image_cdn_url: null,
        cover_image_direct_url: null,
        cover_image_path: null,
      };
    } else {
      updateData = {
        ...updateData,
        cover_image_cdn_url: uploadResult.cover.cdnUrl,
        cover_image_direct_url: uploadResult.cover.b2Url,
        cover_image_path: uploadResult.cover.path,
        gif_cover_cdn_url: null,
        gif_cover_direct_url: null,
        gif_cover_path: null,
        static_gif_cdn_url: null,
        static_gif_direct_url: null,
        static_gif_path: null,
      };
    }

    const { error: updateError } = await supabase
      .from('events')
      .update(updateData)
      .eq('id', eventId);

    if (updateError) throw updateError;

    console.log('✅ Event updated with cover image');
    return true;
  } catch (error) {
    console.error('❌ Error updating event cover image:', error);
    throw error;
  }
};

/**
 * CREATE EVENT
 */
export const createEvent = async (eventData) => {
  try {
    console.log('🆕 Creating event...');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const financialYear = await getCurrentFinancialYear();
    const shareableSlug = await generateUniqueSlug(eventData.title);

    const completeEventData = {
      ...eventData,
      host_id: user.id,
      financial_year: financialYear,
      app_source: APP_SOURCE,
      source_platform: SOURCE_PLATFORM,
      shareable_slug: shareableSlug,
      album_slug: shareableSlug, // Same as shareable_slug initially
      host_pricing_tier_id: eventData.host_pricing_tier_id || 'host-free',
      photo_pricing_tier_id: eventData.photo_pricing_tier_id || 'photo-free',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data: newEvent, error: insertError } = await supabase
      .from('events')
      .insert(completeEventData)
      .select()
      .single();

    if (insertError) throw insertError;

    // Increment hosting count
    try {
      await supabase.rpc('increment_user_hosting_count', {
        user_uuid: user.id
      });
    } catch (rpcError) {
      console.warn('Could not increment hosting count:', rpcError);
    }

    // Update community events count if linked
    if (eventData.community_id) {
      try {
        await supabase.rpc('increment_community_events_count', {
          community_uuid: eventData.community_id
        });
      } catch (rpcError) {
        console.warn('Could not increment community count:', rpcError);
      }
    }

    console.log('✅ Event created with ID:', newEvent.id);
    return newEvent.id;
  } catch (error) {
    console.error('❌ Error creating event:', error);
    throw error;
  }
};

/**
 * UPDATE EVENT
 */
export const updateEvent = async (eventId, eventData) => {
  try {
    console.log('🔄 Updating event...');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check if user is the host
    const { data: existingEvent, error: fetchError } = await supabase
      .from('events')
      .select('host_id')
      .eq('id', eventId)
      .single();

    if (fetchError) throw fetchError;
    if (existingEvent.host_id !== user.id) {
      throw new Error('You do not have permission to edit this event');
    }

    const updateData = {
      ...eventData,
      updated_at: new Date().toISOString(),
    };

    const { data: updatedEvent, error: updateError } = await supabase
      .from('events')
      .update(updateData)
      .eq('id', eventId)
      .select()
      .single();

    if (updateError) throw updateError;

    console.log('✅ Event updated successfully');
    return updatedEvent;
  } catch (error) {
    console.error('❌ Error updating event:', error);
    throw error;
  }
};

/**
 * GET EVENT BY ID
 */
export const getEventById = async (eventId) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('id', eventId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching event:', error);
    return null;
  }
};

/**
 * GET EVENT BY SLUG
 */
export const getEventBySlug = async (slug) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('shareable_slug', slug)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching event by slug:', error);
    return null;
  }
};

/**
 * DELETE EVENT
 */
export const deleteEvent = async (eventId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check if user is the host
    const { data: existingEvent, error: fetchError } = await supabase
      .from('events')
      .select('host_id, cover_image_path, gif_cover_path, static_gif_path')
      .eq('id', eventId)
      .single();

    if (fetchError) throw fetchError;
    if (existingEvent.host_id !== user.id) {
      throw new Error('You do not have permission to delete this event');
    }

    // Delete associated images from storage
    const filesToDelete = [
      existingEvent.cover_image_path,
      existingEvent.gif_cover_path,
      existingEvent.static_gif_path,
    ].filter(Boolean);

    if (filesToDelete.length > 0) {
      await supabase.storage
        .from('event-media')
        .remove(filesToDelete);
    }

    // Delete event
    const { error: deleteError } = await supabase
      .from('events')
      .delete()
      .eq('id', eventId);

    if (deleteError) throw deleteError;

    // Decrement hosting count
    try {
      await supabase.rpc('decrement_user_hosting_count', {
        user_uuid: user.id
      });
    } catch (rpcError) {
      console.warn('Could not decrement hosting count:', rpcError);
    }

    console.log('✅ Event deleted successfully');
    return true;
  } catch (error) {
    console.error('❌ Error deleting event:', error);
    throw error;
  }
};

/**
 * GET USER'S EVENTS
 */
export const getUserEvents = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('host_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching user events:', error);
    return [];
  }
};

/**
 * Search events
 */
export const searchEvents = async (query, filters = {}) => {
  try {
    let queryBuilder = supabase
      .from('events')
      .select('*')
      .ilike('title', `%${query}%`);

    if (filters.eventType) {
      queryBuilder = queryBuilder.eq('event_type', filters.eventType);
    }

    if (filters.privacy) {
      queryBuilder = queryBuilder.eq('privacy', filters.privacy);
    }

    queryBuilder = queryBuilder.order('created_at', { ascending: false });

    const { data, error } = await queryBuilder;

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error searching events:', error);
    return [];
  }
};

export default {
  createEvent,
  updateEvent,
  getEventById,
  getEventBySlug,
  deleteEvent,
  getUserEvents,
  searchEvents,
  uploadEventCoverImage,
  updateEventCoverImage,
  generateUniqueSlug,
  getCurrentFinancialYear,
};